import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Check, CircleCheck, CircleSlash, Pencil, Printer } from 'lucide-react';

import { cancelSignReceipt } from '#/api/aftersalesMgmt/signReceipt/cancel';
import { completeSignReceipt } from '#/api/aftersalesMgmt/signReceipt/complete';
import { establishSignReceipt } from '#/api/aftersalesMgmt/signReceipt/establish';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  getSignReceiptApiErrorMessage,
  isSignReceiptVersionConflict,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/apiError';
import {
  canCancelSignReceipt,
  canCompleteSignReceipt,
  canDownloadSignReceiptExcel,
  canEditSignReceipt,
  canEstablishSignReceipt,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/signReceiptStatus';
import useSignReceiptExcelDownload from '#/modules/crmMgmt/aftersales/signReceipt/hooks/useSignReceiptExcelDownload';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  signReceiptDetailQueryKey,
  signReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const SignReceiptCommandBar = ({ detail, onRefetch }) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? null;
  const status = detail?.status;
  const excelDownload = useSignReceiptExcelDownload(id);
  const canDownloadExcel = canDownloadSignReceiptExcel(status);

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: signReceiptListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: signReceiptDetailQueryKey(id) });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'establish') {
        return establishSignReceipt(id, payload);
      }
      if (action === 'complete') {
        return completeSignReceipt(id, payload);
      }
      if (action === 'cancel') {
        return cancelSignReceipt(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result) => {
      toast.success(result?.message || '操作成功');
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getSignReceiptApiErrorMessage(error));
      if (isSignReceiptVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  if (!id) {
    return null;
  }

  async function handleEstablish() {
    const ok = await confirm({
      title: '成立簽收單',
      message: '成立後將暫時鎖定所選維修品項，確定成立？',
      confirmText: '成立',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'establish', payload: { updatedAt } });
  }

  async function handleComplete() {
    const ok = await confirm({
      title: '完成簽收單',
      message: '完成後鎖定永久生效且不可取消，確定完成？',
      confirmText: '完成',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'complete', payload: { updatedAt } });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: '取消簽收單',
      message: status === 'ESTABLISHED'
        ? '取消後將釋放暫時鎖定，確定取消？'
        : '取消後無法復原，確定取消？',
      confirmText: '取消簽收',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancel', payload: { updatedAt } });
  }

  return (
    <>
      {ConfirmDialogHost}
      {canDownloadExcel ? (
        <Button
          type="button"
          variant="outline"
          disabled={excelDownload.isPrintPending}
          onClick={excelDownload.handlePrint}
        >
          <Printer className="size-3.5" aria-hidden />
          {excelDownload.isPrintPending ? '下載中…' : '列印'}
        </Button>
      ) : null}
      {canEditSignReceipt(status) ? (
        <Button asChild className="document-detail-edit-action">
          <Link to={ROUTES.signReceiptEdit(id)}>
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canEstablishSignReceipt(status) ? (
        <Button disabled={isPending} onClick={handleEstablish}>
          <Check className="size-3.5" aria-hidden />
          成立
        </Button>
      ) : null}
      {canCompleteSignReceipt(status) ? (
        <Button disabled={isPending} onClick={handleComplete}>
          <CircleCheck className="size-3.5" aria-hidden />
          完成
        </Button>
      ) : null}
      {canCancelSignReceipt(status) ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleCancel}
        >
          <CircleSlash className="size-3.5" aria-hidden />
          取消
        </Button>
      ) : null}
    </>
  );
};

SignReceiptCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default SignReceiptCommandBar;
